import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, Input, palette } from '@stats/playbook-components';
import logo from '../components/Header/logo.png';

export type LoginResult = 'invalid' | 'rate_limited' | 'unavailable' | undefined;

export const MESSAGES = {
  invalid: 'Invalid username or password.',
  rate_limited: 'Too many attempts. Try again later.',
  unavailable: 'Sign-in is temporarily unavailable. Try again later.',
  expired: 'Your session has expired. Sign in again.',
} as const;

const Page = styled.main`
  font-family: Roboto, sans-serif;
  box-sizing: border-box;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 24px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #000;
`;

const Card = styled.form`
  box-sizing: border-box;
  background-color: ${palette['sp-white']};
  color: #3d434b;
  border: 1px solid #d5d5d5;
  border-radius: 4px;
  padding: 48px 36px;
  width: 100%;
  max-width: 365px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 380px) {
    padding: 32px 24px;
  }
`;

const Brand = styled.div`
  position: relative;
  width: 182px;
  max-width: 100%;
  aspect-ratio: 320 / 113;
  margin: 0 auto 8px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    filter: brightness(0);
  }

  img + img {
    position: absolute;
    inset: 0;
    filter: none;
    clip-path: inset(0 77% 0 0);
  }
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;
  margin: 0;
`;

const Description = styled.p`
  font-size: 13px;
  line-height: 1.5;
  text-align: center;
  margin: 0 0 4px;
`;

const SubmitButton = styled(Button)`
  && {
    width: 100%;
    max-width: none;
    min-height: 48px;
    margin-top: 8px;
    border-radius: 2px;
    background: #f7192d;
    color: ${palette['sp-white']};
    text-transform: none;
    font-size: 14px;
    font-weight: 400;
  }

  &&:hover:not(:disabled) {
    background: #dc1426;
  }

  &&:disabled {
    opacity: 0.6;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const Fields = styled.div`
  display: grid;
  gap: 16px;

  .input-wrapper {
    position: relative;
    min-width: 0;
  }

  .input-wrapper > label {
    position: absolute;
    top: -7px;
    left: 10px;
    z-index: 1;
    padding: 0 4px;
    background: ${palette['sp-white']};
    font-size: 12px;
    line-height: 14px;
    margin: 0;
  }

  && .inner-wrapper {
    width: 100%;
    background: ${palette['sp-white']};
  }

  && input {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    height: 48px;
    border: 1px solid #8c8c8c;
    border-radius: 2px;
    background: ${palette['sp-white']};
    padding: 12px 14px;
    font-size: 14px;
  }

  input:focus {
    outline: 2px solid #535bff;
    outline-offset: -1px;
  }
`;

const VisibilityLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  cursor: pointer;
`;

const Message = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
`;

type LoginViewProps = {
  notice?: string;
  onLogin: (username: string, password: string) => Promise<LoginResult>;
};

const LoginView: React.FC<LoginViewProps> = ({ notice, onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;

    const submittedPassword = password;
    setPassword('');
    setShowPassword(false);
    setError(undefined);
    setSubmitting(true);

    const result = await onLogin(username, submittedPassword);

    if (mounted.current) {
      setSubmitting(false);
      setError(result ? MESSAGES[result] : undefined);
    }
  };

  return (
    <Page>
      <Card onSubmit={handleSubmit} aria-labelledby="login-title">
        <Brand>
          <img src={logo} alt="Stats Perform" />
          <img src={logo} alt="" aria-hidden="true" />
        </Brand>
        <Title id="login-title">Sign in</Title>
        <Description>Enter your username and password for Stats Perform to continue</Description>
        {notice && !error && <Message role="status">{notice}</Message>}
        {error && <Message role="alert">{error}</Message>}
        <Fields>
        <Input
          id="login-username"
          label="Username"
          aria-label="Username"
          name="username"
          type="text"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          required
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Input
          id="login-password"
          label="Password"
          aria-label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        </Fields>
        <VisibilityLabel>
          <input
            type="checkbox"
            checked={showPassword}
            onChange={(event) => setShowPassword(event.target.checked)}
          />
          Show password
        </VisibilityLabel>
        <SubmitButton type="submit" variant="primary" aria-label="Sign in" disabled={submitting}>
          Continue
        </SubmitButton>
      </Card>
    </Page>
  );
};

export default LoginView;
